import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {Range} from "../../../../shared/model/range-model";
import {OdmEditOptionModalComponent} from "../odm-edit-option-modal/odm-edit-option-modal.component";
import {CommonModalComponent} from "../../../../shared/common-modal/common-modal.component";
import {OdmCopyOptionModalComponent} from "../odm-copy-option-modal/odm-copy-option-modal.component";
import {
  CopyOptionPayload,
  CubicleOption,
  CubicleOptionValue,
  EditOptionPayload, EditOptionValuePayload,
  GetOptionsPayload,
  OptionUsageReference
} from "../../../../shared/model/cubicleOption-model";
import {OptionsService} from "../../../../shared/services/options.service";
import {MessageService} from "primeng/api";
import {CustomTranslateLoader} from "../../../../shared/localization/CustomTranslateLoader";
import {TranslateService} from "@ngx-translate/core";
import {UploadService} from "../../../shared";
import {LoggerService} from "../../../../shared/logging/logger.service";
import {RangeType} from "../../../../shared/model/range-type";
import {OdmEditOptionValueModalComponent} from "../odm-edit-option-value-modal/odm-edit-option-value-modal.component";
import {SessionStorage} from "ngx-webstorage";
import {User} from "../../../../shared/user/user";
import {Localization} from "../../../../shared/model/localization.model";

@Component({
  standalone: false,
  selector: 'app-odm-options-element-list',
  templateUrl: './odm-options-element-list.component.html',
  styleUrls: ['./odm-options-element-list.component.scss']
})
export class OdmOptionsElementListComponent implements OnInit {
  private static readonly NO_CATEGORY_LABEL = 'No Category';

  @SessionStorage()
  public user: User;

  @SessionStorage()
  localization: Localization;

  @Input()
  selectedRange: Range;

  @Output()
  clickBack: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('addOptionModal') addOptionModal: OdmEditOptionModalComponent;
  @ViewChild('editOptionModal') editOptionModal: OdmEditOptionModalComponent;
  @ViewChild('deleteOptionModal') deleteOptionModal: CommonModalComponent;
  @ViewChild('copyOptionModal') copyOptionModal: OdmCopyOptionModalComponent;
  @ViewChild('addOptionValueModal') addOptionValueModal: OdmEditOptionValueModalComponent;
  @ViewChild('editOptionValueModal') editOptionValueModal: OdmEditOptionValueModalComponent;
  @ViewChild('deleteOptionValueModal') deleteOptionValueModal: CommonModalComponent;


  isLoading = false;
  isExportLoading = false;
  options: CubicleOption[] = [];
  displayedOptions: CubicleOption[] = [];
  activeOption: string;
  optionIdToDelete: string;
  optionValueNameToDelete: string;
  associatedOptionUsages: OptionUsageReference[] = [];
  associatedOptionValueUsages: OptionUsageReference[] = [];
  searchValue: string;
  searchPlaceholder: string;

  constructor(private readonly optionsService: OptionsService,
              private readonly messageService: MessageService,
              private readonly customTranslateLoader: CustomTranslateLoader,
              private readonly translateService: TranslateService,
              private readonly uploadService: UploadService,
              private readonly logger: LoggerService) { }

  ngOnInit(): void {
    this.getOptions();
    this.updateLocalization();
    this.searchPlaceholder = this.translateService.instant("T_SEARCH");
  }

  returnOnOfferSelection() {
    this.clickBack.emit();
  }

  getOptions() {
    this.isLoading = true;
    this.optionsService.getOptionsElementsByRangeId(this.selectedRange.id, true).subscribe(options => {
      this.options = options;
      this.displayedOptions = this.options
        .toSorted((a, b) => (this.translateService.instant(a.name) > this.translateService.instant(b.name) ? 1 : -1));
      this.isLoading = false;
    }, () => this.isLoading = false)
  }

  onSuccessEditingOption() {
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('T_INFO'),
      detail: this.translateService.instant('T_OFFER_DATA_MANAGEMENT_SUCCESS_EDITING_OPTION'),
    });

    this.customTranslateLoader.getLastTranslation(this.user.preferredLanguage).subscribe(res => {
      this.translateService.setTranslation(this.user.preferredLanguage, res, true);
      this.getOptions();
    });
  }

  onErrorEditingOption(error) {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('T_ERROR'),
      detail: this.translateService.instant('T_OFFER_DATA_MANAGEMENT_ERROR_EDITING_OPTION') + ': ' + error,
    });
    this.getOptions();
  }

  onSuccessDeletingOption() {
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('T_INFO'),
      detail: this.translateService.instant('T_OFFER_DATA_MANAGEMENT_SUCCESS_DELETING_OPTION'),
    });
    this.optionIdToDelete = null;
    this.getOptions();
  }

  onErrorDeletingOption(error) {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('T_ERROR'),
      detail: this.translateService.instant('T_OFFER_DATA_MANAGEMENT_ERROR_DELETING_OPTION') + ': ' + error,
    });
    this.optionIdToDelete = null;
    this.getOptions();
  }

  onClickAddOptionButton() {
    const languages = this.localization.langs.map(lang => lang.language);
    this.addOptionModal.showAddMode(languages, this.showOnPopUpManagement());
  }

  onClickAddOptionValueButton() {
    this.addOptionValueModal.showAddMode();
  }


  onClickEditOptionValueButton(event: { option: CubicleOption, optionValue: CubicleOptionValue }) {
    this.editOptionValueModal.showEditMode(event.option, event.optionValue);
  }


  addOption(addOptionForm: EditOptionPayload) {
    this.isLoading = true;
    this.activeOption = null;
    addOptionForm.rangeId = this.selectedRange.id;
    this.optionsService.createOption(addOptionForm).subscribe(() => {
      this.onSuccessEditingOption();
      this.activeOption = addOptionForm.id;
    }, (error) => {
      this.onErrorEditingOption(error);
    });
  }

  editOption(editOptionPayload: EditOptionPayload) {
    this.isLoading = true;
    editOptionPayload.rangeId = this.selectedRange.id;
    this.activeOption = editOptionPayload.id;
    this.optionsService.editOption(editOptionPayload).subscribe(() => {
      this.onSuccessEditingOption();
    }, (error) => {
      this.onErrorEditingOption(error);
    });
  }

  copyOption(copyOptionPayload: CopyOptionPayload) {
    this.isLoading = true;
    this.activeOption = null;
    this.optionsService.copyOption(copyOptionPayload).subscribe((optionId) => {
      this.activeOption = optionId.id;
      this.onSuccessEditingOption();
    }, (error) => {
      this.onErrorEditingOption(error);
    });
  }

  deleteOption() {
    this.isLoading = true;
    this.activeOption = null;
    this.optionsService.deleteOptionElement(this.optionIdToDelete).subscribe(() => {
      this.onSuccessDeletingOption();
    }, (error) => {
      this.onErrorDeletingOption(error);
    });
  }

  addOptionValue(editOptionValuePayload: EditOptionValuePayload) {
    this.isLoading = true;
    this.activeOption = editOptionValuePayload.optionId;
    this.optionsService.addOptionValue(editOptionValuePayload).subscribe(() => {
      this.onSuccessEditingOption();
    }, (error) => {
      this.onErrorEditingOption(error);
    });
  }

  editOptionValue(editOptionValuePayload: EditOptionValuePayload) {
    this.isLoading = true;
    this.activeOption = editOptionValuePayload.optionId;
    this.optionsService.editOptionValue(editOptionValuePayload).subscribe(() => {
      this.onSuccessEditingOption();
    }, (error) => {
      this.onErrorEditingOption(error);
    });
  }

  deleteOptionValue() {
    this.isLoading = true;
    this.activeOption = this.optionIdToDelete;
    this.optionsService.deleteOptionValueElement(this.optionIdToDelete, this.optionValueNameToDelete).subscribe(() => {
      this.onSuccessDeletingOption();
    }, (error) => {
      this.onErrorDeletingOption(error);
    });
  }

  onClickEditOptionButton(option: CubicleOption) {
    this.editOptionModal.showEditMode(option, this.showOnPopUpManagement());
  }

  onClickDeleteOptionButton(option: CubicleOption) {
    this.optionIdToDelete = option.id;
    this.loadAssociatedOptionUsages(option.id);
  }
  /**
   * Loads associated usages for a given option and displays them in a modal.
   * @param optionId
   * @private
   */
  private loadAssociatedOptionUsages(optionId: string): void {
    this.loadAssociatedUsages(
      (payload) => this.getOptionAssociationsFromComponents(payload, optionId),
      (usages) => this.associatedOptionUsages = usages,
      this.deleteOptionModal);
  }

  /**
   * Returns the associated usages for a given option from the provided payload.
   * @param payload
   * @param optionId
   * @private
   */
  private getOptionAssociationsFromComponents(payload: GetOptionsPayload, optionId: string): OptionUsageReference[] {
    if (!payload?.optionsByReferences || !optionId) {
      return [];
    }
    return this.buildUsageAssociations( payload.optionsByReferences, (opt) => opt?.id === optionId);
  }

  /**
   * Returns the associated usages for a given option value from the provided payload.
   * @param payload
   * @param optionToDelete
   * @param optionValueToDelete
   * @private
   */
  private getOptionValueAssociations(payload: GetOptionsPayload, optionToDelete: CubicleOption, optionValueToDelete: CubicleOptionValue): OptionUsageReference[] {
    if (!payload?.optionsByReferences || !optionToDelete?.id || !optionValueToDelete?.value) {
      return [];
    }
    const referenceRef = optionValueToDelete.reference?.ref;
    return this.buildUsageAssociations(
      payload.optionsByReferences,
      (opt) => {
        if (opt == null || opt.id !== optionToDelete.id) {
          return false;
        }
        return (opt.values || []).some(value =>
          value && value.value === optionValueToDelete.value &&
          (!referenceRef || (value.reference?.ref === referenceRef))
        );
      });
  }

  /**
   * Builds a list of usage associations based on the provided options and a filter predicate.
   * @param optionsByReferences
   * @param filterPredicate
   * @private
   */
  private buildUsageAssociations(
    optionsByReferences: Map<string, CubicleOption[]>,
    filterPredicate: (option: CubicleOption) => boolean): OptionUsageReference[] {
    const usages = new Map<string, OptionUsageReference>();

    Object.entries(optionsByReferences).forEach(([componentRef, options]) => {
      (options || [])
        .filter((opt) => opt != null && filterPredicate(opt))
        .forEach((opt) => {
          if (opt) {
            const categoryLabel = this.getCategoryLabel(opt);
            const key = `${categoryLabel}__${componentRef}`;
            usages.set(key, {category: categoryLabel, component: componentRef});
          }
        });
    });
    return Array.from(usages.values()).sort((a, b) => a.component.localeCompare(b.component));
  }
  onClickCopyOptionButton(option: CubicleOption) {
    this.copyOptionModal.show(option);
  }

  onClickDeleteOptionValueButton(event: { option: CubicleOption, optionValue: CubicleOptionValue }) {
    this.optionIdToDelete = event.option.id;
    this.optionValueNameToDelete = event.optionValue.value;
    this.loadAssociatedOptionValueUsages(event.option, event.optionValue);
  }
  /**
   * Loads associated usages for a given option value and displays them in a modal.
   * @param option
   * @param optionValue
   * @private
   */
  private loadAssociatedOptionValueUsages(option: CubicleOption, optionValue: CubicleOptionValue): void {
    this.loadAssociatedUsages(
      (payload) => this.getOptionValueAssociations(payload, option, optionValue),
      (usages) => this.associatedOptionValueUsages = usages,
      this.deleteOptionValueModal);
  }

  /**
   * Returns the translated category label for the given option, or a default label if no category is present.
   * @param option
   * @private
   */
  private getCategoryLabel(option: CubicleOption): string {
    return option?.category?.name
      ? this.translateService.instant(option.category.name)
      : OdmOptionsElementListComponent.NO_CATEGORY_LABEL;
  }

  /**
   * Loads associated usages for a given option or option value and displays them in a modal.
   * @param fetchUsages
   * @param setUsages
   * @param modal
   * @private
   */
  private loadAssociatedUsages(
    fetchUsages: (payload: GetOptionsPayload) => OptionUsageReference[],
    setUsages: (usages: OptionUsageReference[]) => void,
    modal: CommonModalComponent
  ): void {
    this.optionsService.getOptionsByRangeId(this.selectedRange.id, true).subscribe({
      next: (payload: GetOptionsPayload) => {
        setUsages(fetchUsages(payload));
        modal.show();
      },
      error: () => {
        setUsages([]);
        modal.show();
      }
    });
  }

  onClickExportOptionsButton() {
    this.logger.info('Export options of range: ' + this.translateService.instant(this.selectedRange.nameKey));
    this.isExportLoading = true;
    this.optionsService.exportOptionsOfRange(this.selectedRange.id).subscribe(
      response => {
        this.isExportLoading = false;
        this.uploadService.createXlsFileFromBackendResponse(response);
      }, () => {
        this.isExportLoading = false;
      }
    );

  }

  showOnPopUpManagement(): boolean {
    return this.selectedRange.rangeType !== RangeType.TRANSFORMER
      && this.selectedRange.rangeType !== RangeType.IMPORTED;
  }

  searchInputChanged() {
    if (!this.options)
      return;

    this.displayedOptions = this.options.filter(option => {
      // check ref name
      if (this.translateService.instant(option.name).toLowerCase().includes(this.searchValue.toLowerCase()))
        return true;
      if (option.description.toLowerCase().includes(this.searchValue.toLowerCase()))
        return true;
      return option.values.some(value => {
        // check option value name
        if (this.translateService.instant(value.value).toLowerCase().includes(this.searchValue.toLowerCase()))
          return true;
        // check option value ref
        if (value.reference && value.reference.ref && value.reference.ref.toLowerCase().includes(this.searchValue.toLowerCase()))
          return true;
        return false;
      })
    })
  }

  updateLocalization(): void {
    const matchedLanguage = this.localization.langs.find(lang => lang.language === this.user.preferredLanguage);
    if (matchedLanguage) {
      this.customTranslateLoader.getTranslation(this.user.preferredLanguage, matchedLanguage.available).subscribe(translations => {
        this.translateService.setTranslation(this.user.preferredLanguage, translations, true);
      });
    }
  }

  onChangeOptionsValueOrder(event: { optionId: string; optionValueName: string; currentIndex: number }) {
    this.isLoading = true;
    this.activeOption = event.optionId;
    this.optionsService.editOptionValueOrder(event.optionId, event.optionValueName, event.currentIndex).subscribe(() => {
      this.onSuccessEditingOption();
    }, (error) => {
      this.onErrorEditingOption(error);
    })
  }
}

